import * as path from 'path';

import type * as zod from 'zod';

import type { Camera } from '@sb/integrations/implementations/Camera/implementation/Camera';
import { makeNamespacedLog } from '@sb/log';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';
import { loadHostIdentity } from '@sb/service-interfaces/host/identity';

import type { Expression } from '../../types';
import type { StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments from './Arguments';
import { namingExpression } from './captureFileName';
import {
  formatCaptureTimestamp,
  saveImageToStorage,
  saveMetadataToStorage,
} from './captureUtils';
import { nameFrame, readFileNameParts } from './fileNameParts';
import Variables from './Variables';

const log = makeNamespacedLog('captureStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class CaptureStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = false;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public initializeVariableState(): void {
    this.variables = {
      latestSaveFilepath: null,
      captureTimestamp: null,
    };
  }

  /**
   * If `saveMetadata` is enabled, builds a metadata object for the captured
   * image and uploads it alongside the image as a JSON sidecar file.
   */
  private async maybeSaveMetadata(
    remotePath: string,
    storageEquipment: { kind: string; executeCommand: (...args: any[]) => any },
    nameValues: Record<string, string> | undefined,
  ): Promise<void> {
    if (!this.args.saveMetadata) {
      return;
    }

    const identity = await loadHostIdentity();

    const {
      saveEquipmentId: _saveEquipmentId,
      saveFilepath: _saveFilepath,
      saveMetadata: _saveMetadata,
      fileName: _fileName,
      ...stepParameters
    } = this.args;

    await saveMetadataToStorage({
      metadata: {
        imageFilename: path.basename(remotePath),
        botId: this.routineContext.robotId,
        botAlias: identity?.alias,
        timestamp: new Date().toISOString(),
        stepKind: 'Capture',
        nameValues,
        stepParameters,
      },
      remotePath,
      storageEquipment,
    });
  }

  private async evaluateFilepath(expression: Expression): Promise<string> {
    // Evaluate the filepath expression to get the dynamic filename
    const remotePath = await this.routineFrame.evaluateExpression(expression);

    // Ensure the evaluated filepath is a string
    if (typeof remotePath !== 'string') {
      throw new Error(
        `Filepath expression must evaluate to a string, received ${typeof remotePath}`,
      );
    }

    if (!remotePath) {
      throw new Error(
        'Filepath expression must evaluate to a non-empty string',
      );
    }

    if (!remotePath.endsWith('.png')) {
      throw new Error(`Filepath must end with .png (Filepath: ${remotePath})`);
    }

    return remotePath;
  }

  public async _play({ fail }: StepPlayArguments): Promise<void> {
    try {
      log.info('capture.play', 'Playing Capture step');

      const useCppCameraBot = getFeatureFlagSync('useCppCameraBot');

      let image: ArrayBuffer;

      if (useCppCameraBot) {
        if (!this.args.cameraEquipmentId) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No camera equipment ID provided',
          });
        }

        const cameraEquipment = this.routineContext.equipment.getDeviceById(
          this.args.cameraEquipmentId,
        ) as Camera | undefined;

        if (!cameraEquipment) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: `Camera equipment with id ${this.args.cameraEquipmentId} not found`,
          });
        }

        image = await cameraEquipment.getColorFrame();
      } else {
        // Use old WristCamera implementation
        const wristCamera = this.routineContext.equipment.getWristCamera();

        if (!wristCamera) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'No wrist camera found in Equipment',
          });
        }

        if (!this.args.camera) {
          return fail({
            failure: {
              kind: FailureKind.CameraFailure,
            },
            failureReason: 'Camera configuration is required',
          });
        }

        image = await wristCamera.getColorFrame(this.args.camera);
      }

      // If an ROI was provided, crop the image before saving
      let imageToSave: ArrayBuffer = image;

      if (this.args.regionOfInterest) {
        const preprocessed = await this.routineContext.vision.preprocessImage(
          image,
          this.args.regionOfInterest,
        );

        imageToSave = preprocessed.croppedImage;

        log.info('capture.play', 'Applied region of interest to image');
      }

      log.info('capture.play', 'Image captured, converting to PNG');

      const expression = namingExpression(this.args.saveFilepath);

      let remotePath: string;
      let nameValues: Record<string, string> | undefined;

      if (expression) {
        // Initialize capture timestamp (available for expressions)
        const formattedTimestamp = formatCaptureTimestamp(new Date());
        this.setVariable('captureTimestamp', formattedTimestamp);

        remotePath = await this.evaluateFilepath(expression);
      } else {
        const { name, frameName } = nameFrame({
          stepKind: 'Capture',
          fileNameParts: readFileNameParts(this.args.fileName, this),
        });

        this.setVariable('captureTimestamp', frameName.captureTimestamp);
        remotePath = `${name}.png`;
        nameValues = frameName.nameValues;
      }

      // Get the storage equipment (FTP Server or S3 Bucket)
      const storageEquipment = this.routineContext.equipment.getDeviceById(
        this.args.saveEquipmentId,
      );

      if (!storageEquipment) {
        throw new Error(
          `Storage equipment not found: ${this.args.saveEquipmentId}`,
        );
      }

      // Validate, convert to PNG, upload, and clean up
      const validationFailure = await saveImageToStorage({
        image: imageToSave,
        remotePath,
        storageEquipment,
      });

      if (validationFailure) {
        return fail(validationFailure);
      }

      this.setVariable('latestSaveFilepath', remotePath);

      await this.maybeSaveMetadata(remotePath, storageEquipment, nameValues);

      log.info('capture.play', 'Capture step completed');
    } catch (error) {
      log.error('capture.play', 'Capture failed', error);

      // convertImageToPng marks image format errors with failureKind
      if ((error as any).failureKind === FailureKind.CameraFailure) {
        return fail({
          failure: {
            kind: FailureKind.CameraFailure,
          },
          failureReason: error.message,
          error,
        });
      }

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'Capture',
        },
        failureReason: `${error.message}`,
        error,
      });
    }
  }
}
